import { useState } from 'react'
import { useArchivedDeals } from '../../api/queries/deals'
import type { DealFilters } from '../../api/queries/deals'
import { userFacingMessage } from '../../api/errors'
import { EmptyState, Skeleton } from '../../ui/Feedback'
import { DealCard } from './DealCard'
import styles from './Board.module.css'

/**
 * The archive: everything put away, in one flat grid.
 *
 * No columns, because neither stage nor decision is the question being asked
 * here — the question is "what did I archive, and do I want one of them back?".
 * It pages like a column does, for the same reason.
 */
const ARCHIVE_PAGE = 40

export function ArchivedList({ filters }: { filters: DealFilters }) {
  const [limit, setLimit] = useState(ARCHIVE_PAGE)
  const page = useArchivedDeals(filters, limit)
  const deals = page.data?.deals ?? []
  const total = page.data?.total ?? 0
  const hidden = Math.max(total - deals.length, 0)

  if (page.isPending) return <Skeleton height={180} />
  if (page.isError) return <EmptyState title="Could not load the archive" body={userFacingMessage(page.error)} />
  if (total === 0) {
    return <EmptyState title="No archived deals" body="Deals you archive, and deals you mark won or lost, appear here." />
  }

  return (
    <>
      <section className={styles.archive} aria-label={`Archived deals, ${total}`}>
        {deals.map((deal) => (
          <DealCard key={deal.id} deal={deal} />
        ))}
      </section>
      {hidden > 0 ? (
        <button type="button" className={styles.more} onClick={() => setLimit((current) => current + ARCHIVE_PAGE)}>
          Show {hidden} more
        </button>
      ) : null}
    </>
  )
}
